import React, { memo } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';

interface SelectionHeaderProps {
  /** Number of currently selected items */
  selectedCount: number;
  /** Total number of items available for selection */
  totalCount: number;
  /** Callback when select all/none checkbox is toggled */
  onToggleSelectAll: () => void;
  /** Optional label for items (default: "items") */
  itemLabel?: string;
  /** Optional right-side content (e.g., pagination) */
  rightContent?: React.ReactNode;
}

/**
 * A reusable header component for selection-based lists.
 * Displays a checkbox for select all/none with a count indicator.
 */
const SelectionHeader = memo(function SelectionHeader({
  selectedCount,
  totalCount,
  onToggleSelectAll,
  itemLabel = 'items',
  rightContent,
}: SelectionHeaderProps) {
  const isAllSelected = selectedCount === totalCount && totalCount > 0;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={onToggleSelectAll}
          size="small"
          inputProps={{
            'aria-label': isAllSelected ? `Deselect all ${itemLabel}` : `Select all ${itemLabel}`,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {selectedCount > 0 ? `${selectedCount} selected` : `Select all`}
        </Typography>
      </Box>
      {rightContent}
    </Box>
  );
});

export default SelectionHeader;
